// @flow
import { useCallback, useState, useRef } from 'react';
import useUpdateAffordaPortalDetailsMutation from 'hooks/mutations/useUpdateAffordaPortalDetailsMutation';
import useAffordaPortalDetailsQuery from 'hooks/queries/useAffordaPortalDetailsQuery';
import type { SectionsProgress } from 'hooks/sectionsProgress/types';
import useEnableSectionsProgressLogging from 'hooks/sectionsProgress/useEnableSectionsProgressLogging';
import debugLogger from 'utils/logger';

const useSectionsProgressData = (): ({
  isFetched: boolean,
  sectionsProgress: SectionsProgress,
  setSectionsProgress: (SectionsProgress: SectionsProgress) => void,
}) => {
  const { isSectionsProgressLoggingEnabled } =
    useEnableSectionsProgressLogging();
  const shouldUpdateRef = useRef(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [sectionsProgress, setSectionsProgressState] =
    useState<SectionsProgress>({});

  const logger = useCallback(
    (message, data, status) => {
      if (!isSectionsProgressLoggingEnabled) return;
      debugLogger('Sections Progress data', message, data, status);
    },
    [isSectionsProgressLoggingEnabled],
  );

  const { isLoading } = useAffordaPortalDetailsQuery({
    options: {
      onSuccess: (response) => {
        logger('Database Response', response);

        if (shouldUpdateRef.current) {
          const hasData = Boolean(
            response?.sectionsProgress &&
              Object.keys(response?.sectionsProgress)?.length,
          );

          if (hasData) {
            setSectionsProgressState(response.sectionsProgress);

            if (isSectionsProgressLoggingEnabled) {
              logger(
                'Initialized sectionsProgress',
                {
                  sectionsProgress: response.sectionsProgress,
                },
                'success',
              );
            }
          } else {
            logger('Initialized with no data', response, 'info');
          }

          setIsInitialized(true);
        } else {
          logger('Should not update section data');
        }
      },
    },
  });

  const { mutate } = useUpdateAffordaPortalDetailsMutation();

  const setSectionsProgress = useCallback(
    (sectionsProgressUpdate: SectionsProgress) => {
      if (isSectionsProgressLoggingEnabled) {
        logger(
          'Set State & DB sectionsProgress',
          {
            sectionsProgress: sectionsProgressUpdate,
          },
          'success',
        );
      }
      shouldUpdateRef.current = false;
      setSectionsProgressState(() => {
        mutate({ sectionsProgress: sectionsProgressUpdate });
        return sectionsProgressUpdate;
      });
    },
    [isSectionsProgressLoggingEnabled, logger, mutate],
  );

  const deleteSectionsProgress = useCallback(() => {
    shouldUpdateRef.current = true;
    logger('Delete State & DB sectionsProgress', {}, 'success');
    mutate({ sectionsProgress: {} });
  }, [logger, mutate]);

  return {
    isInitialized,
    isLoading,
    deleteSectionsProgress,
    sectionsProgress,
    setSectionsProgress,
  };
};

export default useSectionsProgressData;
